import type { AppStoreState } from '@runtime/core/store/appStoreTypes'
import type { AppStoreSlice } from '@runtime/core/store/slices/sliceTypes'
import {
  DEFAULT_THEME_ID,
  normalizeAvatarColor,
  normalizeLocale,
  normalizeStrategyHelpMode,
  normalizeThemeTokens,
  pickInitialArcadeEnergyStartLevel,
  pickInitialArcadeFusionObstacles,
  pickInitialArcadeFusionTimeMin,
  pickInitialArcadeIntroSeen,
  pickInitialArcadeSfxEnabled,
  pickInitialArcadeVibrationEnabled,
  pickInitialAvatarColor,
  pickInitialLocale,
  pickInitialNick,
  pickInitialPosterPrivateUnlockedAll,
  pickInitialPosterPrivateUnlockedIds,
  pickInitialStrategyHelpMode,
  pickInitialThemeId,
  pickInitialThemeTokens,
  randomGuestNick,
} from '@runtime/core/store/appStorePersist'

type PrefsSlice = Pick<
  AppStoreState,
  | 'themeId'
  | 'themeTokens'
  | 'themeRev'
  | 'locale'
  | 'strategyHelpMode'
  | 'onlineNick'
  | 'onlineAvatarColor'
  | 'onlineSoundEnabled'
  | 'arcadeIntroSeen'
  | 'arcadeSfxEnabled'
  | 'arcadeVibrationEnabled'
  | 'arcadeEnergyStartLevel'
  | 'arcadeFusionObstacles'
  | 'arcadeFusionTimeMin'
  | 'posterPrivateUnlockedAll'
  | 'posterPrivateUnlockedIds'
  | 'setThemeId'
  | 'setThemeTokens'
  | 'bumpThemeRev'
  | 'setLocale'
  | 'setStrategyHelpMode'
  | 'setOnlineNick'
  | 'setOnlineAvatarColor'
  | 'setOnlineSoundEnabled'
  | 'toggleOnlineSoundEnabled'
  | 'setArcadeIntroSeen'
  | 'setArcadeSfxEnabled'
  | 'setArcadeVibrationEnabled'
  | 'setArcadeEnergyStartLevel'
  | 'setArcadeFusionObstacles'
  | 'setArcadeFusionTimeMin'
  | 'setPosterPrivateUnlockedAll'
  | 'addPosterPrivateUnlockedId'
  | 'clearPosterPrivateUnlocked'
>

export const createPrefsSlice: AppStoreSlice<PrefsSlice> = (set) => ({
  themeId: pickInitialThemeId(),
  themeTokens: pickInitialThemeTokens(),
  themeRev: 0,
  locale: pickInitialLocale(),
  strategyHelpMode: pickInitialStrategyHelpMode(),
  onlineNick: pickInitialNick(),
  onlineAvatarColor: pickInitialAvatarColor(),
  onlineSoundEnabled: false,
  arcadeIntroSeen: pickInitialArcadeIntroSeen(),
  arcadeSfxEnabled: pickInitialArcadeSfxEnabled(),
  arcadeVibrationEnabled: pickInitialArcadeVibrationEnabled(),
  arcadeEnergyStartLevel: pickInitialArcadeEnergyStartLevel(),
  arcadeFusionObstacles: pickInitialArcadeFusionObstacles(),
  arcadeFusionTimeMin: pickInitialArcadeFusionTimeMin(),
  posterPrivateUnlockedAll: pickInitialPosterPrivateUnlockedAll(),
  posterPrivateUnlockedIds: pickInitialPosterPrivateUnlockedIds(),

  setThemeId: (id: string) => {
    const next = String(id || '').trim() || DEFAULT_THEME_ID
    set({ themeId: next })
  },

  setThemeTokens: (tokens: string[] | null) => {
    const norm = normalizeThemeTokens(tokens)
    set({ themeTokens: norm })
  },

  bumpThemeRev: () => {
    set((s) => ({ themeRev: (s.themeRev || 0) + 1 }))
  },

  setLocale: (locale: string) => {
    const picked = normalizeLocale(locale)
    set({ locale: picked })
  },

  setStrategyHelpMode: (mode) => {
    set({ strategyHelpMode: normalizeStrategyHelpMode(mode) })
  },

  setOnlineNick: (nick: string) => {
    const next = String(nick || '').trim() || randomGuestNick()
    set({ onlineNick: next })
  },

  setOnlineAvatarColor: (color) => {
    const next = normalizeAvatarColor(color)
    set({ onlineAvatarColor: next })
  },

  setOnlineSoundEnabled: (enabled: boolean) => {
    set({ onlineSoundEnabled: !!enabled })
  },

  toggleOnlineSoundEnabled: () => {
    set((s) => ({ onlineSoundEnabled: !s.onlineSoundEnabled }))
  },

  setArcadeIntroSeen: (seen: boolean) => {
    set({ arcadeIntroSeen: !!seen })
  },

  setArcadeSfxEnabled: (enabled: boolean) => {
    set({ arcadeSfxEnabled: !!enabled })
  },

  setArcadeVibrationEnabled: (enabled: boolean) => {
    set({ arcadeVibrationEnabled: !!enabled })
  },

  setArcadeEnergyStartLevel: (level: number) => {
    const n = Number(level)
    set({
      arcadeEnergyStartLevel: Number.isFinite(n)
        ? Math.max(1, Math.floor(n))
        : 1,
    })
  },

  setArcadeFusionObstacles: (enabled: boolean) => {
    set({ arcadeFusionObstacles: !!enabled })
  },

  setArcadeFusionTimeMin: (minutes: number) => {
    const n = Number(minutes)
    const safe = Number.isFinite(n) ? Math.floor(n) : 5
    set({ arcadeFusionTimeMin: Math.max(1, Math.min(30, safe)) })
  },

  setPosterPrivateUnlockedAll: (value) => {
    set({ posterPrivateUnlockedAll: !!value })
  },

  addPosterPrivateUnlockedId: (themeId) => {
    const nextId = String(themeId || '').trim()
    if (!nextId) return

    set((s) => {
      if (s.posterPrivateUnlockedIds.includes(nextId)) return {}
      return {
        posterPrivateUnlockedIds: [...s.posterPrivateUnlockedIds, nextId],
      }
    })
  },

  clearPosterPrivateUnlocked: () => {
    set({ posterPrivateUnlockedAll: false, posterPrivateUnlockedIds: [] })
  },
})
